import React, { FC, useEffect } from 'react';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Printer } from '~reducers';
import { TroubleShootButtons, TroubleShootingTextWrapper } from '../AddedPrinter';
import Button from '~ComponentsImproved/Button';
import { Box } from '~Styles';
import * as actions from '~actions';
import { useDispatch, useI18n, useLogger } from '~Hooks';

type Data = {
  title?: string;
  text?: string;
  images?: {
    src: string;
    height?: number;
  }[];
  buttons?: Array<{
    text: string;
    action: string;
  }>;
};
interface Props {
  printer: Printer;
  closeGuide: () => void;
  goToContactSupport: () => void;
  assignStation: () => void;
  sessionId: string;
}

const BongsNotPrinting: FC<Props> = ({
  printer,
  closeGuide,
  goToContactSupport,
  assignStation,
  sessionId,
}) => {
  const logger = useLogger('printer-troubleshooting');
  const { i18n } = useI18n();
  const [storage, setStorage] = useStorage();
  const dispatch = useDispatch();

  useEffect(() => {
    logger.info('Troubleshooting step: BONGS_NOT_PRINTING', {
      troubleShooting: {
        printer,
        bongPrintsEnabled: storage.enableBongPrints,
        KDSBongOnProgress: storage.KDSBongOnProgress,
        troubleShootingSessionId: sessionId,
      },
    });
  }, [storage, printer.stations]);

  const triggerAction = (action: string) => {
    switch (action) {
      case 'CLOSE_GUIDE':
        closeGuide();
        break;
      case 'SEND_TO_ASSIGN_STATION':
        logger.info('Troubleshooting: send to assign station', {
          troubleShootingSessionId: sessionId,
        });
        assignStation();
        break;
      case 'ENABLE_BONG_PRINTS':
        logger.info('Troubleshooting: enabling bong prints', {
          troubleShootingSessionId: sessionId,
        });
        setStorage({
          enableBongPrints: true,
        });
        dispatch(
          actions.addToast(
            i18n('Printer.TS.Toast.TicketsEnabled'),
            true,
            'kitchen-tickets-enabled',
          ),
        );
        closeGuide();
        break;
      case 'CONTACT_SUPPORT':
        goToContactSupport();
        break;
    }
  };

  const Component = ({ data }: { data: Data }) => {
    return (
      <>
        <TroubleShootingTextWrapper>
          {data.title && (
            <Heading level={1} color="#fff" bottom={16}>
              {data.title}
            </Heading>
          )}
          {data.text && (
            <Paragraph level={2} color="var(--colorBlueGray200)">
              {data.text}
            </Paragraph>
          )}
        </TroubleShootingTextWrapper>

        {data.images &&
          data.images.map((image) => (
            <Box marginTop="small">
              <img src={image.src} height={image.height ?? 'auto'} alt={data.title} />
            </Box>
          ))}

        {data.buttons && (
          <TroubleShootButtons>
            {data.buttons.map((button) => (
              <Button
                key={button.text}
                text={button.text}
                styleType="OK"
                onTouchStart={() => {
                  triggerAction(button.action);
                }}
              />
            ))}
          </TroubleShootButtons>
        )}
      </>
    );
  };

  // No station assigned to the printer
  if (!printer.stations.length) {
    return (
      <Component
        data={{
          title: i18n('Printer.TS.NoStations'),
          text: i18n('Printer.TS.NoStations.Desc'),
          buttons: [
            {
              text: i18n('Printer.Troubleshooting.OK'),
              action: 'CLOSE_GUIDE',
            },
            {
              text: i18n('Printer.TS.AssignStation'),
              action: 'SEND_TO_ASSIGN_STATION',
            },
          ],
        }}
      />
    );
  }

  // Bong prints are disabled
  if (!storage.enableBongPrints) {
    return (
      <Component
        data={{
          title: i18n('Printer.TS.TicketsDisabled'),
          text: i18n('Printer.TS.TicketsDisabled.Question'),
          buttons: [
            {
              text: i18n('Printer.Troubleshooting.No'),
              action: 'CLOSE_GUIDE',
            },
            {
              text: i18n('Printer.Troubleshooting.Yes'),
              action: 'ENABLE_BONG_PRINTS',
            },
          ],
        }}
      />
    );
  }

  // Bong prints are disabled
  if (storage.KDSBongOnOrderStatus) {
    return (
      <Component
        data={{
          title: i18n('Printer.TS.KDSBongOnOrderStatus') + storage.KDSBongOnOrderStatus,
          text: i18n('Printer.TS.KDSBongOnOrderStatus.Desc'),
          buttons: [
            {
              text: i18n('Printer.Troubleshooting.Close'),
              action: 'CLOSE_GUIDE',
            },
            {
              text: i18n('Printer.TS.AnotherIssue'),
              action: 'CONTACT_SUPPORT',
            },
          ],
        }}
      />
    );
  }

  // Bong prints are disabled
  if (storage.KDSBongOnProgress) {
    return (
      <Component
        data={{
          title: i18n('Printer.TS.KDSBongOnProgress'),
          text: i18n('Printer.TS.KDSBongOnProgress.Desc'),
          buttons: [
            {
              text: i18n('Printer.Troubleshooting.Close'),
              action: 'CLOSE_GUIDE',
            },
            {
              text: i18n('Printer.TS.AnotherIssue'),
              action: 'CONTACT_SUPPORT',
            },
          ],
        }}
      />
    );
  }

  return (
    <Component
      data={{
        title: i18n('Printer.TS.CheckSettings'),
        text: i18n('Printer.TS.CheckSettings.Desc'),
        buttons: [
          {
            text: i18n('Printer.Troubleshooting.Close'),
            action: 'CLOSE_GUIDE',
          },
          {
            text: i18n('Printer.Troubleshooting.NeedMoreHelp'),
            action: 'CONTACT_SUPPORT',
          },
        ],
      }}
    />
  );
};

export default BongsNotPrinting;
